import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import "./SignUpDetailsScreen.css";
import "../authFlow.css";
import ReactPixel from "react-facebook-pixel";
import TagManager from "react-gtm-module";
import {
  car_sign_up,
  icon_normal,
  kirkos_logo_white_yellow,
} from "../../../assets/images";
import "bootstrap/dist/css/bootstrap.min.css";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CustomInput, Progressbar } from "../../../components";
import { useForm } from "react-hook-form";
import rules from "../../../rules";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trackEvent } from "../../../services/events";
import { API_URL } from "../../../services/apiConfig";
import axiosInstance from "../../../services/axios.instance";
import { Country, CountryState, RegistrationData } from "../../../types";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RegistrationSuccessModal from "../../../components/RegistrationSuccessModal";
import { ErrorModal } from "../../../components";
import { toast } from "react-toastify";
import SEO from "../../../components/SEO";
import DraggableWhatsAppIcon from "../../../components/CustomerDashBoard/DraggableWhatsAppIcon";

interface LocationData {
  latitude: number;
  longitude: number;
}

const API_LOCATIONIQ_TOKEN = process.env.REACT_APP_LOCATIONIQ_TOKEN;

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface MapClickHandlerProps {
  onClick: (lat: number, lon: number) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

interface MapUpdaterProps {
  center: [number, number];
}

const MapUpdater: React.FC<MapUpdaterProps> = ({ center }) => {
  const map = useMap();

  React.useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);

  return null;
};

const SignUpDetails = () => {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [state1, setState1] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [stateData, setStateData] = useState<CountryState[]>([]);
  const [stateTouched, setStateTouched] = useState(false);
  const [buildingFlatNumber, setBuildingFlatNumber] = useState<string>("");
  const [addressComponents, setAddressComponents] = useState<{
    street: string;
    city: string;
    district: string;
    pin_code: string;
  }>({ street: "", city: "", district: "", pin_code: "" });
  const [formErrors, setFormErrors] = useState<{
    firstName?: string;
    lastName?: string;
    country?: string;
    state?: string;
    address?: string;
    buildingFlatNumber?: string;
  }>({});

  const [selectedLocation, setSelectedLocation] = useState<LocationData>({
    latitude: 25.2048,
    longitude: 55.2708,
  });
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    25.2048,
    55.2708,
  ]);
  const [selectedAddressName, setSelectedAddressName] = useState<string>("");
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState<string>("");
  const [mapSearchResults, setMapSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingCurrentLocation, setIsLoadingCurrentLocation] =
    useState(false);
  const [suggestionClicked, setSuggestionClicked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { state } = useLocation();
  const phoneNum = state && state.phoneNum;

  const navigate = useNavigate();

  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const countryRef = useRef<HTMLSelectElement>(null);
  const stateRef = useRef<HTMLSelectElement>(null);
  const buildingRef = useRef<HTMLInputElement>(null);

  const handleKeyPress = (
    e: React.KeyboardEvent,
    nextRef: React.RefObject<any>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef.current?.focus();
    }
  };

  const handleInputChange = (name: string, value: string) => {
    switch (name) {
      case "password":
        setPassword(value);
        break;
      case "email":
        setEmail(value);
        break;
      default:
        break;
    }
  };

  const {
    control,
    formState: { isValid },
    setValue,
    getValues,
    trigger,
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    const trackPageLoad = async () => {
      if (!(window as any).fbq && process.env.REACT_APP_META_PIXEL_ID) {
        ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
      }
      ReactPixel.track("ViewContent", {
        content_type: "page",
        content_name: "Customer Info Screen",
        content_category: "SignUp",
      });
      await trackEvent({
        event_name: {
          web: "Kirkos_GetCustomerInfoScreen",
          app: "Kirkos_GetCustomerInfoScreen_App",
        },
        event_category: "SignUp",
        module: "Registration",
        payload: {},
      });
    };
    trackPageLoad();
  }, []);

  useEffect(() => {
    if (password && confirmPassword && password === confirmPassword) {
      setError(null);
    }
  }, [password, confirmPassword]);

  // Prevent browser autofill on this page only — set directly on DOM nodes
  useEffect(() => {
    const fields: Array<React.RefObject<HTMLInputElement>> = [
      passwordRef,
      confirmPasswordRef,
      emailRef,
      firstNameRef,
      lastNameRef,
      buildingRef,
    ];
    fields.forEach((ref) => {
      if (ref.current) {
        ref.current.setAttribute("autocomplete", "new-password");
      }
    });
  }, []);


  const reverseGeocodeMapLocation = async (lat: number, lon: number) => {
    setIsLoadingAddress(true);
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/reverse?key=${API_LOCATIONIQ_TOKEN}&lat=${lat}&lon=${lon}&format=json`,
      );
      const data = await response.json();
      const addressName = data?.display_name || "Selected Location";
      const address = data?.address || {};
      setAddressComponents({
        street: address?.road || address?.street || "",
        city: address?.city || address?.town || address?.village || "",
        district: address?.county || address?.state_district || "",
        pin_code: address?.postcode || "",
      });

      setSelectedAddressName(addressName);
      setValue("contactAddress", addressName);
      setSuggestionClicked(true);
      setIsLoadingAddress(false);
      return addressName;
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setIsLoadingAddress(false);
      return "Selected Location";
    }
  };

  const handleMapClick = async (lat: number, lon: number) => {
    setSelectedLocation({ latitude: lat, longitude: lon });
    setMapCenter([lat, lon]);
    trackEvent({ event_name: "Kirkos_SignUp_Map_Pin_Drop", event_category: "SignUp", module: "Registration", payload: { lat, lon } }).catch(() => { });
    if (process.env.REACT_APP_META_PIXEL_ID) { try { ReactPixel.track("AddToWishlist", { content_name: "Map Pin Drop", content_category: "SignUp" }); } catch (_) { } }
    await reverseGeocodeMapLocation(lat, lon);
  };

  const handleMapSearch = async (query: string) => {
    setMapSearchQuery(query);

    if (query.length < 3) {
      setMapSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/search?key=${API_LOCATIONIQ_TOKEN}&q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=ae`,
      );
      const data = await response.json();
      setMapSearchResults(data || []);
      setIsSearching(false);
    } catch (error) {
      console.error("Error searching location:", error);
      setMapSearchResults([]);
      setIsSearching(false);
    }
  };

  const handleSearchResultClick = async (result: any) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    setSelectedLocation({ latitude: lat, longitude: lon });
    setMapCenter([lat, lon]);

    const address = result?.address || {};
    setAddressComponents({
      street: address?.road || address?.street || "",
      city: address?.city || address?.town || address?.village || "",
      district: address?.county || address?.state_district || "",
      pin_code: address?.postcode || "",
    });

    setSelectedAddressName(result.display_name);
    setValue("contactAddress", result.display_name);
    setSuggestionClicked(true);
    setMapSearchQuery("");
    setMapSearchResults([]);
    trackEvent({ event_name: "Kirkos_SignUp_Map_Search_Result_Selected", event_category: "SignUp", module: "Registration", payload: { address: result.display_name } }).catch(() => { });
    if (process.env.REACT_APP_META_PIXEL_ID) { try { ReactPixel.track("Search", { content_name: result.display_name, content_category: "SignUp Map" }); } catch (_) { } }
    TagManager.dataLayer({
      dataLayer: {
        event: "search_submit",
        page_type: "standard",
        page_path: window.location.pathname,
        page_url: window.location.href,
        search_source: "signup_map",
        pickup_location: result.display_name
      }
    });
  };

  const handleGetCurrentLocationMap = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsLoadingCurrentLocation(true);
    trackEvent({ event_name: "Kirkos_SignUp_Use_Current_Location", event_category: "SignUp", module: "Registration", payload: {} }).catch(() => { });
    if (process.env.REACT_APP_META_PIXEL_ID) { try { ReactPixel.track("CustomizeProduct", { content_name: "Use Current Location", content_category: "SignUp" }); } catch (_) { } }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedLocation({ latitude, longitude });
        setMapCenter([latitude, longitude]);
        await reverseGeocodeMapLocation(latitude, longitude);
        setIsLoadingCurrentLocation(false);
      },
      (geoError) => {
        console.error("Error getting location:", geoError);
        alert("Unable to retrieve your location. Please enter manually.");
        setIsLoadingCurrentLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const handleSubmit = async () => {
    const errors: any = {};

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      toast.error("Passwords do not match");
      return;
    }

    if (!firstName) errors.firstName = "First name is required";
    if (!lastName) errors.lastName = "Last name is required";
    if (!country) errors.country = "Country is required";
    if (!state1) errors.state = "State is required";
    if (!suggestionClicked) {
      errors.address = "Please select a location on the map";
    }
    if (!buildingFlatNumber) {
      errors.buildingFlatNumber = "Building/Flat number is required";
    }

    setFormErrors(errors);
    trigger();
    setStateTouched(true);

    if (Object.keys(errors).length > 0 || !isValid) {
      toast.error("Please fill in all required fields correctly");
      trackEvent({ event_name: "Kirkos_SignUp_Form_Validation_Failed", event_category: "SignUp", module: "Registration", payload: { errors: Object.keys(errors) } }).catch(() => { });
      if (process.env.REACT_APP_META_PIXEL_ID) { try { ReactPixel.track("SignUpFormError", { fields: Object.keys(errors).join(",") }); } catch (_) { } }
      return;
    }

    if (!country || !state1 || !isValid || !suggestionClicked || !buildingFlatNumber) {
      return;
    }

    if (!(window as any).fbq && process.env.REACT_APP_META_PIXEL_ID) {
      ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
    }
    ReactPixel.track("Lead", {
      content_type: "registration_form",
      content_name: "Customer Info Submit",
      content_category: "SignUp",
    });
    await trackEvent({
      event_name: {
        web: "Kirkos_CustomerInfoSubmitButton",
        app: "Kirkos_CustomerInfoSubmitButton_App",
      },
      event_category: "SignUp",
      module: "Registration",
      payload: {},
    });

    setError(null);
    setIsSubmitting(true);

    // Read UTM attribution params saved by the marketing landing page
    const storedUtmParams = (() => {
      try {
        return JSON.parse(localStorage.getItem("kirkos_meta_utm_params") || "{}");
      } catch {
        return {};
      }
    })();

    handleRegistration.mutate({
      phone_no: phoneNum,
      password,
      user_type: "CONSUMER",
      email,
      firstname: firstName,
      lastname: lastName,
      last_level: 1,
      customer_address: {
        address_1: buildingFlatNumber,
        street: addressComponents.street,
        city: addressComponents.city,
        district: addressComponents.district,
        state: state1,
        country,
        pin_code: addressComponents.pin_code,
        latitude: selectedLocation.latitude.toString(),
        longitude: selectedLocation.longitude.toString(),
        location_info: getValues("contactAddress"),
        address_type: "HOME",
      },
      ...((storedUtmParams.ad_objective_name || storedUtmParams.publisher_platform || storedUtmParams.campaign_id) && {
        installReferrer_model: {
          ...(storedUtmParams.ad_objective_name && { ad_objective_name: storedUtmParams.ad_objective_name }),
          ...(storedUtmParams.publisher_platform && { publisher_platform: storedUtmParams.publisher_platform }),
          ...(storedUtmParams.campaign_id && { campaign_name: storedUtmParams.campaign_id }),
        }
      }),
    });
  };

  const handleRegistration = useMutation({
    mutationFn: (userData: RegistrationData) =>
      axiosInstance.post(API_URL.REGISTER, userData),
    onSuccess: async (successResponse) => {
      setIsSubmitting(false);

      await trackEvent({
        event_name: {
          web: "Kirkos_SignUp_Success",
          app: "Kirkos_SignUp_Success_App",
        },
        event_category: "SignUp",
        module: "Registration",
        payload: {
          status: "success",
          message: successResponse?.data?.message ?? "",
        },
      });

      if (!(window as any).fbq && process.env.REACT_APP_META_PIXEL_ID) {
        ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
      }
      ReactPixel.track("CompleteRegistration", {
        content_type: "registration",
        content_name: "Customer Registration",
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNum,
        email,
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "signup_complete",
          page_type: "standard",
          page_path: window.location.pathname,
          page_url: window.location.href,
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNum,
          email: email
        }
      });

      if (successResponse.status && successResponse?.data?.message) {
        setShowSuccessModal(true);
      }
    },
    onError: () => {
      setIsSubmitting(false);
      setShowErrorModal(true);
    },
  });

  const { data: countriesData } = useQuery<Country[], Error>({
    queryKey: ["countries"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.COUNTRY);
      return response.data;
    },
  });

  useEffect(() => {
    if (countriesData && countriesData.length > 0 && !country) {
      const firstCountry = countriesData[0];
      const countryNameValue = Array.isArray(firstCountry?.countryName)
        ? firstCountry?.countryName?.[0]
        : firstCountry?.countryName;
      setCountry(countryNameValue || "");
      setSelectedCountryId(firstCountry?.countryId?.toString() || "");

      axiosInstance
        .get(`${API_URL.STATE}${firstCountry?.countryId}`)
        .then((response) => {
          setStateData(response?.data);
        })
        .catch((fetchError) => {
          console.error("Error loading states:", fetchError);
        });
    }
  }, [countriesData, country]);

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        navigate("/signin");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal, navigate]);

  const handleCountryChange = async (e: any) => {
    const selectedCountryName = e.target.value;
    setCountry(selectedCountryName);
    trackEvent({ event_name: "Kirkos_SignUp_Country_Selected", event_category: "SignUp", module: "Registration", payload: { country: selectedCountryName } }).catch(() => { });
    if (process.env.REACT_APP_META_PIXEL_ID) { try { ReactPixel.track("CountrySelected", { country: selectedCountryName }); } catch (_) { } }

    const selectedCountry = countriesData?.find((countryItem: Country) => {
      const countryName = Array.isArray(countryItem?.countryName)
        ? countryItem?.countryName?.[0]
        : countryItem?.countryName;
      return countryName === selectedCountryName;
    });

    if (selectedCountry) {
      setSelectedCountryId(selectedCountry?.countryId?.toString());
      const response = await axiosInstance.get(
        `${API_URL.STATE}${selectedCountry?.countryId}`,
      );
      setStateData(response?.data);
    }
  };

  const togglePasswordVisibility1 = () => {
    setShowPassword1(!showPassword1);
  };

  const togglePasswordVisibility2 = () => {
    setShowPassword2(!showPassword2);
  };

  const handleConfirmPassword = (value: string) => {
    setConfirmPassword(value);
    if (password !== value) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
  };

  const detailsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Complete Registration | Kirkos Car Rental",
    "description": "Provide your details to complete your Kirkos car rental registration."
  };

  return (
    <div className="customer-auth-page signup-details-page-override">
      <SEO
        title="Account Details | Complete Your Kirkos Registration"
        description="Finalize your Kirkos account setup by providing your profile information. Securely manage your car rental preferences."
        keywords="kirkos registration, account setup, car rental profile"
        schemaData={detailsSchema}
      />
      <div className="customer-auth-shell">
        <div className="customer-auth-brand-panel">
          <div className="customer-auth-brand-topbar">
            <Link to="/" className="customer-auth-logo-link" aria-label="Kirkos home">
              <img
                src={kirkos_logo_white_yellow}
                alt="Kirkos"
                className="customer-auth-logo"
              />
            </Link>
            <div className="customer-auth-brand-links">
              <Link to="/" className="customer-auth-brand-link">
                Home
              </Link>
              <Link to="/signin" className="customer-auth-brand-link">
                Login
              </Link>
            </div>
          </div>

          <div className="customer-auth-brand-content">
            <div>
              <span className="customer-auth-eyebrow">Final setup</span>
              <h1 className="customer-auth-brand-title">
                Complete your account and get ready to book with confidence.
              </h1>
              <p className="customer-auth-brand-copy">
                Add your password, profile details, and home address so Kirkos
                can deliver a faster and more polished rental experience from
                your very first booking.
              </p>
            </div>

            <div className="customer-auth-brand-highlights">
              <div className="customer-auth-highlight-card">
                <span className="customer-auth-highlight-value">Step 3</span>
                <span className="customer-auth-highlight-label">
                  Final account details before activation
                </span>
              </div>
              <div className="customer-auth-highlight-card">
                <span className="customer-auth-highlight-value">Profile</span>
                <span className="customer-auth-highlight-label">
                  Save the essentials you need for a smoother checkout
                </span>
              </div>
              <div className="customer-auth-highlight-card">
                <span className="customer-auth-highlight-value">Ready</span>
                <span className="customer-auth-highlight-label">
                  Move straight into booking once registration is complete
                </span>
              </div>
            </div>

            <div className="customer-auth-car-stage">
              <div className="customer-auth-car-glow" />
              <img
                src={car_sign_up}
                className="customer-auth-car-image"
                alt="Kirkos account setup"
              />
            </div>
          </div>
        </div>

        <div className="customer-auth-card">
          <div className="customer-auth-card-head">
            <div className="customer-auth-progress">
              <Progressbar currentStage={3} />
            </div>
            <div className="customer-auth-card-icon-wrap">
              <img
                src={icon_normal}
                className="customer-auth-card-icon"
                alt="Customer details"
              />
            </div>
            <span className="customer-auth-card-kicker">Profile details</span>
            <h2 className="customer-auth-heading">Finish your registration</h2>
            <p className="customer-auth-subcopy">
              Create your password and add the contact information needed to
              activate your Kirkos customer account.
            </p>
          </div>

          <div className="customer-auth-scroll-area overflow-y-auto">
            {/* Section 1: Security Setup */}
            <div className="customer-auth-section-header">
              <span className="customer-auth-section-title">Security setup</span>
              <div className="customer-auth-section-line" />
            </div>

            <div className="customer-auth-field-block">
              <label className="customer-auth-label">
                Create a password <span className="customer-auth-required">*</span>
              </label>
              <div
                className="customer-auth-input-shell"
                onClick={() => passwordRef.current?.focus()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              >
                <CustomInput
                  inputRef={passwordRef}
                  inputName="password"
                  inputType={showPassword1 ? "text" : "password"}
                  className="customer-auth-input"
                  placeholder="Enter your password"
                  editable={true}
                  onInputChange={(value: string) =>
                    handleInputChange("password", value)
                  }
                  control={control}
                  rules={rules.AuthRules.password}
                  autoFocus={true}
                  onKeyDown={(e) => handleKeyPress(e, confirmPasswordRef)}

                />
                <button
                  type="button"
                  className="customer-auth-password-toggle"
                  onClick={togglePasswordVisibility1}
                >
                  {showPassword1 ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="customer-auth-field-block">
              <label className="customer-auth-label">
                Confirm password <span className="customer-auth-required">*</span>
              </label>
              <div
                className="customer-auth-input-shell"
                onClick={() => confirmPasswordRef.current?.focus()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              >
                <CustomInput
                  inputRef={confirmPasswordRef}
                  inputName="confirmpassword"
                  inputType={showPassword2 ? "text" : "password"}
                  className="customer-auth-input"
                  placeholder="Re-enter your password"
                  editable={true}
                  onInputChange={handleConfirmPassword}
                  control={control}
                  onKeyDown={(e) => handleKeyPress(e, emailRef)}
                />
                <button
                  type="button"
                  className="customer-auth-password-toggle"
                  onClick={togglePasswordVisibility2}
                >
                  {showPassword2 ? <EyeSlash /> : <Eye />}
                </button>
              </div>
              {error && <p className="customer-auth-error">{error}</p>}
            </div>

            {/* Section 2: Personal Profile */}
            <div className="customer-auth-section-header">
              <span className="customer-auth-section-title">Personal info</span>
              <div className="customer-auth-section-line" />
            </div>

            <div className="customer-auth-field-block">
              <label className="customer-auth-label">
                Email address <span className="customer-auth-required">*</span>
              </label>
              <div
                className="customer-auth-input-shell"
                onClick={() => emailRef.current?.focus()}
              >
                <CustomInput
                  inputRef={emailRef}
                  inputName="email"
                  inputType="email"
                  className="customer-auth-input"
                  placeholder="abc@gmail.com"
                  editable={true}
                  onInputChange={(value: string) =>
                    handleInputChange("email", value)
                  }
                  control={control}
                  rules={rules.AuthRules.email}
                  onKeyDown={(e) => handleKeyPress(e, firstNameRef)}
                />
              </div>
            </div>

            <div className="customer-auth-field-grid">
              <div className="customer-auth-field-block">
                <label className="customer-auth-label">
                  First name <span className="customer-auth-required">*</span>
                </label>
                <div
                  className="customer-auth-input-shell"
                  onClick={() => firstNameRef.current?.focus()}
                >
                  <input
                    ref={firstNameRef}
                    type="text"
                    className="customer-auth-text-input"
                    placeholder="First name"
                    autoComplete="off"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setFirstName(e.target.value)
                    }
                    onKeyDown={(e) => handleKeyPress(e, lastNameRef)}
                  />
                </div>
                {(stateTouched || formErrors.firstName) && !firstName && (
                  <span className="customer-auth-error">
                    {formErrors.firstName || "required"}
                  </span>
                )}
              </div>

              <div className="customer-auth-field-block">
                <label className="customer-auth-label">
                  Last name <span className="customer-auth-required">*</span>
                </label>
                <div
                  className="customer-auth-input-shell"
                  onClick={() => lastNameRef.current?.focus()}
                >
                  <input
                    ref={lastNameRef}
                    type="text"
                    className="customer-auth-text-input"
                    placeholder="Last name"
                    autoComplete="off"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setLastName(e.target.value)
                    }
                    onKeyDown={(e) => handleKeyPress(e, countryRef)}
                  />
                </div>
                {(stateTouched || formErrors.lastName) && !lastName && (
                  <span className="customer-auth-error">
                    {formErrors.lastName || "required"}
                  </span>
                )}
              </div>
            </div>

            {/* Section 3: Home Address */}
            <div className="customer-auth-section-header">
              <span className="customer-auth-section-title">Home address</span>
              <div className="customer-auth-section-line" />
            </div>

            <div className="customer-auth-field-grid">
              <div className="customer-auth-field-block">
                <label className="customer-auth-label">
                  Country <span className="customer-auth-required">*</span>
                </label>
                <div
                  className="customer-auth-input-shell"
                  onClick={(e) => {
                    if (e.target !== countryRef.current) {
                      countryRef.current?.focus();
                      if (countryRef.current && "showPicker" in countryRef.current) {
                        try {
                          (countryRef.current as any).showPicker();
                        } catch (err) { }
                      }
                    }
                  }}
                >
                  <select
                    ref={countryRef}
                    className="customer-auth-select"
                    value={country}
                    onChange={handleCountryChange}
                    onKeyDown={(e) => handleKeyPress(e, stateRef)}
                  >
                    <option value="">Select country</option>
                    {countriesData &&
                      countriesData.map((countryItem: Country) => {
                        const countryName = Array.isArray(countryItem?.countryName)
                          ? countryItem?.countryName?.[0]
                          : countryItem?.countryName;
                        return (
                          <option
                            key={countryItem?.countryId}
                            value={countryName || ""}
                          >
                            {countryName || ""}
                          </option>
                        );
                      })}
                  </select>
                </div>
                {(stateTouched || formErrors.country) && !country && (
                  <span className="customer-auth-error">
                    {formErrors.country || "required"}
                  </span>
                )}
              </div>

              <div className="customer-auth-field-block">
                <label className="customer-auth-label">
                  State <span className="customer-auth-required">*</span>
                </label>
                <div
                  className="customer-auth-input-shell"
                  onClick={(e) => {
                    if (e.target !== stateRef.current) {
                      stateRef.current?.focus();
                      if (stateRef.current && "showPicker" in stateRef.current) {
                        try {
                          (stateRef.current as any).showPicker();
                        } catch (err) { }
                      }
                    }
                  }}
                >
                  <select
                    ref={stateRef}
                    className="customer-auth-select"
                    value={state1}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setState1(e.target.value)
                    }
                    onKeyDown={(e) => handleKeyPress(e, buildingRef)}
                  >
                    <option value="">Select state</option>
                    {stateData &&
                      stateData.map((stateItem) => (
                        <option key={stateItem.id} value={stateItem.state_name}>
                          {stateItem.state_name}
                        </option>
                      ))}
                  </select>
                </div>
                {(stateTouched || formErrors.state) && !state1 && (
                  <span className="customer-auth-error">
                    {formErrors.state || "required"}
                  </span>
                )}
              </div>
            </div>

            <div className="customer-auth-field-block">
              <label className="customer-auth-label">
                Building / Flat / House Number{" "}
                <span className="customer-auth-required">*</span>
              </label>
              <div
                className="customer-auth-input-shell"
                onClick={() => buildingRef.current?.focus()}
              >
                <input
                  ref={buildingRef}
                  type="text"
                  className="customer-auth-text-input"
                  placeholder="e.g., Flat 12B, Sunrise Apartments"
                  autoComplete="off"
                  value={buildingFlatNumber}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setBuildingFlatNumber(e.target.value)
                  }
                />
              </div>
              {(stateTouched || formErrors.buildingFlatNumber) &&
                !buildingFlatNumber && (
                  <span className="customer-auth-error">
                    {formErrors.buildingFlatNumber || "required"}
                  </span>
                )}
            </div>

            <div className="customer-auth-field-block">
              <label className="customer-auth-label">
                Contact address - select on map{" "}
                <span className="customer-auth-required">*</span>
              </label>

              <div className="customer-auth-map-search-combined">
                <div className="customer-auth-map-input-wrap">
                  <input
                    type="text"
                    className="customer-auth-map-search-field"
                    placeholder="Search for a location in UAE..."
                    value={mapSearchQuery}
                    onChange={(e) => handleMapSearch(e.target.value)}
                  />
                  {isSearching && (
                    <div className="customer-auth-map-search-spinner" />
                  )}
                  {mapSearchResults.length > 0 && (
                    <div className="customer-auth-map-results-overlay">
                      {mapSearchResults.map((result, index) => (
                        <div
                          key={index}
                          onClick={() => handleSearchResultClick(result)}
                          className="customer-auth-map-result-item"
                        >
                          {result.display_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="customer-auth-map-loc-btn"
                  onClick={handleGetCurrentLocationMap}
                  disabled={isLoadingCurrentLocation}
                >
                  {isLoadingCurrentLocation ? "Locating..." : "Use Current Location"}
                </button>
              </div>

              {selectedAddressName && !isLoadingAddress && (
                <div className="customer-auth-address-display">
                  <div className="customer-auth-address-tag">Selected</div>
                  <p className="customer-auth-address-text">{selectedAddressName}</p>
                </div>
              )}

              {(stateTouched || formErrors.address) && !suggestionClicked && (
                <span className="customer-auth-error">
                  {formErrors.address || "Please select a location on the map"}
                </span>
              )}

              <div className="customer-auth-map-container">
                <div className="customer-auth-map-frame">
                  <MapContainer
                    center={mapCenter}
                    zoom={11}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={true}
                    zoomControl={false}
                    attributionControl={false}
                  >
                    <ZoomControl position="bottomright" />
                    <TileLayer
                      attribution=""
                      url={`https://tiles.locationiq.com/v3/streets/r/{z}/{x}/{y}.png?key=${API_LOCATIONIQ_TOKEN}`}
                    />
                    <Marker
                      position={[
                        selectedLocation.latitude,
                        selectedLocation.longitude,
                      ]}
                      draggable={true}
                      eventHandlers={{
                        dragend: (e) => {
                          const marker = e.target;
                          const position = marker.getLatLng();
                          handleMapClick(position.lat, position.lng);
                        },
                      }}
                    />
                    <MapClickHandler onClick={handleMapClick} />
                    <MapUpdater center={mapCenter} />
                  </MapContainer>
                </div>
              </div>
            </div>

            <button
              className="customer-auth-button-primary"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? "Activating account..." : "Complete account"}
            </button>

            <div className="customer-auth-bottom-support">
              <span className="customer-auth-support-tag">Account activation</span>
              <p className="customer-auth-support-msg">
                Once submitted, your Kirkos customer account will be ready for
                sign-in and booking without disruption.
              </p>
            </div>
          </div>
        </div>
      </div>

      <RegistrationSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onNavigateToDashboard={() => {
          navigate("/signin");
        }}
      />
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message="Password not valid"
      />
      <DraggableWhatsAppIcon />
    </div>
  );
};

export default SignUpDetails;
